/**
 * Vacaciones (0118), feriados/descansos laborados (0107) y subsidio (0916)
 * sobre una base salarial (sueldo PLAME o sueldo_interno).
 */

function normalizarFechaYYYYMMDD(fecha) {
  if (!fecha) return null;
  if (typeof fecha === 'string') return fecha.split('T')[0];
  try {
    return new Date(fecha).toISOString().split('T')[0];
  } catch {
    return null;
  }
}

function esFeriadoLaborado(tipo) {
  const t = String(tipo || '').toLowerCase().trim().replace(/\s+/g, '_');
  return t === 'feriado_laborado';
}

/**
 * @param {Array} tareos
 * @param {Set<string>|string[]} fechasFeriadoPagoDoble
 */
export function contarDiasEspeciales(tareos = [], fechasFeriadoPagoDoble = []) {
  const setFechas = fechasFeriadoPagoDoble instanceof Set
    ? fechasFeriadoPagoDoble
    : new Set(
        (fechasFeriadoPagoDoble || [])
          .map((f) => normalizarFechaYYYYMMDD(typeof f === 'object' ? f.fecha : f))
          .filter(Boolean)
      );

  const dias = {
    vacaciones: 0,
    feriado_laborado: 0,
    feriado_laborado_pago_doble: 0,
    descanso_laborado: 0,
    subsidio_por_enfermedad: 0,
    licencia_con_goce_de_haber: 0,
  };

  (tareos || []).forEach((tareo) => {
    const tipo = tareo.tipo_evento || tareo.tipoEvento;
    if (tipo === 'vacaciones') {
      dias.vacaciones++;
    } else if (esFeriadoLaborado(tipo)) {
      const fechaTareo = normalizarFechaYYYYMMDD(tareo.fecha);
      if (fechaTareo && setFechas.has(fechaTareo)) {
        dias.feriado_laborado_pago_doble++;
      } else {
        dias.feriado_laborado++;
      }
    } else if (tipo === 'descanso_laborado') {
      dias.descanso_laborado++;
    } else if (tipo === 'subsidio_por_enfermedad') {
      dias.subsidio_por_enfermedad++;
    } else if (tipo === 'licencia_con_goce_de_haber') {
      dias.licencia_con_goce_de_haber++;
    }
  });

  return dias;
}

/** (sueldo / días_mes) × días, con la misma lógica de mes único / dos meses que PLAME. */
export function montoProporcionalPorDias(sueldoBase, dias, analisisPeriodo) {
  const sueldo = Number(sueldoBase) || 0;
  const nDias = Number(dias) || 0;
  if (sueldo <= 0 || nDias <= 0 || !analisisPeriodo) return 0;

  if (analisisPeriodo.tipo === 'mes_unico') {
    const diasDelMes = Number(analisisPeriodo.mes?.diasCompletosDelMes) || 30;
    if (diasDelMes <= 0) return 0;
    return parseFloat(((sueldo / diasDelMes) * nDias).toFixed(2));
  }

  if (analisisPeriodo.tipo === 'dos_meses') {
    const diasPrimerMes = Number(analisisPeriodo.primerMes?.diasCompletosDelMes) || 0;
    const diasSegundoMes = Number(analisisPeriodo.segundoMes?.diasCompletosDelMes) || 0;
    const diasMesPrincipal = Math.max(diasPrimerMes, diasSegundoMes, 30);
    return parseFloat(((sueldo / diasMesPrincipal) * nDias).toFixed(2));
  }

  return 0;
}

/**
 * Días de calendario del empleado en el período de planilla (ingreso/cese),
 * misma idea que `calcularDiasEfectivos` de planillaCalculoService.
 *
 * @param {object} analisisPeriodo - { fechaInicio, fechaFin, ... }
 * @param {{ fechaIngresoActual?: string, fechaCeseActual?: string|null }} historial
 * @returns {number}
 */
export function calcularDiasEfectivosIngresoCese(analisisPeriodo, historial) {
  if (!analisisPeriodo?.fechaInicio || !analisisPeriodo?.fechaFin) return 0;
  const fechaIngresoRaw = historial?.fechaIngresoActual;
  if (!fechaIngresoRaw) {
    // Sin fecha de ingreso: asumir todo el período (tope 30 lo aplica el llamador)
    const ini = String(analisisPeriodo.fechaInicio).split('T')[0];
    const fin = String(analisisPeriodo.fechaFin).split('T')[0];
    const [yi, mi, di] = ini.split('-').map(Number);
    const [yf, mf, df] = fin.split('-').map(Number);
    const a = new Date(yi, mi - 1, di);
    const b = new Date(yf, mf - 1, df);
    return Math.max(0, Math.ceil((b - a) / (1000 * 60 * 60 * 24)) + 1);
  }

  const fechaInicioPlanillaTexto = String(analisisPeriodo.fechaInicio).split('T')[0];
  const fechaFinPlanillaTexto = String(analisisPeriodo.fechaFin).split('T')[0];
  const fechaIngresoEmpleadoTexto = String(fechaIngresoRaw).split('T')[0];

  const toLocalDate = (ymd) => {
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  const fechaInicioPlanilla = toLocalDate(fechaInicioPlanillaTexto);
  const fechaFinPlanilla = toLocalDate(fechaFinPlanillaTexto);
  const fechaIngresoEmpleado = toLocalDate(fechaIngresoEmpleadoTexto);

  if (fechaIngresoEmpleado > fechaFinPlanilla) return 0;

  const fechaInicioEfectiva =
    fechaIngresoEmpleado > fechaInicioPlanilla ? fechaIngresoEmpleado : fechaInicioPlanilla;

  let fechaFinEfectiva = fechaFinPlanilla;
  if (historial?.fechaCeseActual) {
    const fechaCeseEmpleado = toLocalDate(String(historial.fechaCeseActual).split('T')[0]);
    if (fechaCeseEmpleado < fechaInicioPlanilla) return 0;
    if (fechaCeseEmpleado < fechaFinPlanilla) {
      fechaFinEfectiva = fechaCeseEmpleado;
    }
  }

  const diasEfectivos =
    Math.ceil((fechaFinEfectiva - fechaInicioEfectiva) / (1000 * 60 * 60 * 24)) + 1;
  return Math.max(0, diasEfectivos);
}

/**
 * Prorratea el sueldo básico como PLAME: mes parcial por ingreso/cese + vacaciones
 * (límite 30, ajuste febrero).
 *
 * @param {number} sueldoCompleto
 * @param {number} diasVacaciones
 * @param {object} analisisPeriodo
 * @param {number|null} [diasMaximosPeriodoCalculados] días por historial (ingreso/cese); si null, asume mes completo
 */
export function prorratearSueldoPorVacaciones(
  sueldoCompleto,
  diasVacaciones,
  analisisPeriodo,
  diasMaximosPeriodoCalculados = null
) {
  const sueldo = Number(sueldoCompleto) || 0;
  if (sueldo <= 0 || !analisisPeriodo) return sueldo;

  const vac = Math.max(0, Number(diasVacaciones) || 0);

  let diasBaseMes = 30;
  let esFebrero = false;

  if (analisisPeriodo.tipo === 'mes_unico') {
    diasBaseMes = Number(analisisPeriodo.mes?.diasCompletosDelMes) || 30;
    esFebrero = Number(analisisPeriodo.mes?.numero) === 2;
  } else if (analisisPeriodo.tipo === 'dos_meses') {
    const d1 = Number(analisisPeriodo.primerMes?.diasCompletosDelMes) || 0;
    const d2 = Number(analisisPeriodo.segundoMes?.diasCompletosDelMes) || 0;
    diasBaseMes = Math.max(d1, d2, 30);
  }

  const diasCalc =
    diasMaximosPeriodoCalculados != null && !Number.isNaN(Number(diasMaximosPeriodoCalculados))
      ? Number(diasMaximosPeriodoCalculados)
      : diasBaseMes;

  const diasMaximosPeriodo = Math.min(30, diasCalc);
  const mesCompleto = diasMaximosPeriodo >= Math.min(30, diasBaseMes);

  // Mes completo y sin vacaciones → sueldo íntegro (igual que PLAME)
  if (vac <= 0 && mesCompleto) return sueldo;

  let diasDespues = diasMaximosPeriodo - vac;
  if (esFebrero && vac > 0) {
    diasDespues = Math.min(30, diasDespues + 2);
  }
  let diasEfectivos = Math.min(30, Math.min(diasMaximosPeriodo, diasDespues));
  if (esFebrero && (diasEfectivos === 28 || diasEfectivos === 29)) {
    diasEfectivos = 30;
  }
  diasEfectivos = Math.max(0, diasEfectivos);

  return parseFloat(((sueldo / diasBaseMes) * diasEfectivos).toFixed(2));
}

function pushIngreso(lista, { codigo, concepto, id, monto }) {
  if (!(Number(monto) > 0)) return;
  lista.push({
    codigoPlame: codigo,
    codigo_plame: codigo,
    codigo,
    concepto,
    descripcion: concepto,
    id: id ?? null,
    monto: Number(monto),
    plame_configuracion_id: id ?? null,
    tipo_concepto: 'ingreso',
    origenCalculo: 'dias_especiales_interna',
  });
}

function idPlame(conceptosPlame, codigo) {
  const lista = Array.isArray(conceptosPlame)
    ? conceptosPlame
    : Object.values(conceptosPlame || {});
  const found = lista.find(
    (c) => String(c.codigo_plame || c.codigo) === String(codigo)
  );
  return found?.id ?? null;
}

/**
 * Genera ingresos 0118 / 0107 / 0916 / 0907 (simple) sobre sueldoBase.
 * @returns {{ ingresos: Array, diasEspeciales: object }}
 */
export function calcularIngresosDiasEspeciales({
  sueldoBase,
  tareos = [],
  analisisPeriodo,
  fechasFeriadoPagoDoble = [],
  conceptosPlame = [],
} = {}) {
  const diasEspeciales = contarDiasEspeciales(tareos, fechasFeriadoPagoDoble);
  const ingresos = [];
  const sueldo = Number(sueldoBase) || 0;

  if (sueldo > 0 && analisisPeriodo) {
    if (diasEspeciales.vacaciones > 0) {
      pushIngreso(ingresos, {
        codigo: '0118',
        concepto: 'REMUNERACIÓN VACACIONAL',
        id: idPlame(conceptosPlame, '0118') ?? 46,
        monto: montoProporcionalPorDias(sueldo, diasEspeciales.vacaciones, analisisPeriodo),
      });
    }

    const equivFeriados =
      diasEspeciales.feriado_laborado
      + (diasEspeciales.feriado_laborado_pago_doble * 2)
      + diasEspeciales.descanso_laborado;
    if (equivFeriados > 0) {
      pushIngreso(ingresos, {
        codigo: '0107',
        concepto: 'TRABAJO EN DÍA FERIADO O DÍA DE DESCANSO',
        id: idPlame(conceptosPlame, '0107') ?? 35,
        monto: montoProporcionalPorDias(sueldo, equivFeriados, analisisPeriodo),
      });
    }

    if (diasEspeciales.subsidio_por_enfermedad > 0) {
      pushIngreso(ingresos, {
        codigo: '0916',
        concepto: 'SUBSIDIOS DE INCAPACIDAD POR ENFERMEDAD',
        id: idPlame(conceptosPlame, '0916'),
        monto: montoProporcionalPorDias(
          sueldo,
          diasEspeciales.subsidio_por_enfermedad,
          analisisPeriodo
        ),
      });
    }

    if (diasEspeciales.licencia_con_goce_de_haber > 0) {
      pushIngreso(ingresos, {
        codigo: '0907',
        concepto: 'LICENCIA CON GOCE DE HABER',
        id: idPlame(conceptosPlame, '0907'),
        monto: montoProporcionalPorDias(
          sueldo,
          diasEspeciales.licencia_con_goce_de_haber,
          analisisPeriodo
        ),
      });
    }
  }

  return { ingresos, diasEspeciales };
}

/** Códigos que se recalculan para boleta interna (no deben arrastrarse desde PLAME). */
export const CODIGOS_DIAS_ESPECIALES = ['0118', '0107', '0916', '0907'];

export function esCodigoDiaEspecial(codigo) {
  return CODIGOS_DIAS_ESPECIALES.includes(String(codigo || ''));
}

export default calcularIngresosDiasEspeciales;
